import { Collapse, Icon } from "@blueprintjs/core"
import { TimePicker } from "@blueprintjs/datetime"
import { useToggle } from "@mantine/hooks"
import CustomButton from "components/Atoms/CustomButton"
import CustomCard from "components/Atoms/CustomCard"
import CustomDateInput from "components/Atoms/CustomDateInput"
import CustomDialog from "components/Atoms/CustomDialog"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import Divider from "components/Atoms/Divider"
import RRuleGenerator from "components/Atoms/RRuleGenerator"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FormController from "components/Molecules/FormController"
import {
  RouteAssignmentDetailQuery,
  useCreateUpdateRouteAssignmentMutation,
} from "generated/graphql"
import { GROUPS, ROUTES, USERS } from "graphql/selector.queries.gql"
import { isEmpty, pick } from "lodash-es"
import useRouteStore from "pages/fsm/routes.store"
import useFSMWalkthrough from "pages/fsm/routes/walkthrough.store"
import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import AppToaster from "utils/AppToaster"

type BeatFormProsp = {
  data?: RouteAssignmentDetailQuery["routeAssignmentDetail"]
  isEditable: boolean
  toggleEditable: (value?: React.SetStateAction<boolean>) => void
}
const BeatForm = ({ data, isEditable, toggleEditable }: BeatFormProsp) => {
  const [isOpen, toggle] = useToggle([true, false])
  const [isDialogOpen, toggleDialog] = useToggle()
  const { state: walkState, setState } = useFSMWalkthrough()
  const store = useRouteStore()
  const state = store.data
  const navigate = useNavigate()

  const [createOrUpdate, { loading }] = useCreateUpdateRouteAssignmentMutation({
    onCompleted({ addUpdateRouteAssignment: { routeAssignment } }) {
      AppToaster.show({
        message: `Beat Saved successfully!`,
        intent: "success",
        icon: "tick",
      })
      navigate(`/d/fsm/beat/${routeAssignment.id}`)
      toggleEditable(false)
      setTimeout(() => {
        setState({ run: true })
      }, 400)
    },
    onError(error) {
      return AppToaster.show({
        message: error.message,
        intent: "danger",
        icon: "cross",
      })
    },
  })

  useEffect(() => {
    if (!isEmpty(data)) {
      store.initialiseState({
        step: "select",
        ...pick(data, [
          "id",
          "startDate",
          "startTime",
          "endTime",
          "route",
          "recurrence",
          "asignee",
        ]),
      })
    }

    return function cleanup() {
      store.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return (
    <CustomCard className="bg-white shadow-none p-0 text-sm rounded-lg">
      <div
        onClick={() => toggle()}
        className="p-3 cursor-pointer select-none flex justify-between items-center"
      >
        <span>
          <Icon icon="chevron-down" /> Beat Information
        </span>

        {isEditable ? (
          <div>
            <CustomButton
              onClick={e => {
                e.stopPropagation()
                toggleEditable(false)
              }}
              className="mr-3"
              icon="cross"
              text={"Cancel"}
            />
            <CustomButton
              id="fsm-save-beat"
              onClick={e => {
                e.stopPropagation()
                setState({ run: false, stepIndex: walkState.stepIndex + 1 })
                createOrUpdate({
                  variables: {
                    id: state.id,
                    assigneeId: state.asignee?.id,
                    startDate: state.startDate,
                    // Convert time to 24 hours format
                    startTime: state.startTime?.toLocaleTimeString?.("en-GB"),
                    endTime: state.endTime?.toLocaleTimeString?.("en-GB"),
                    recurrences: state.recurrence,
                    routeId: state.route?.id,
                    shouldTrack: state.shouldTrack,
                  },
                })
              }}
              loading={loading}
              icon={"endorsed"}
              text={"Save"}
              intent="success"
            />
          </div>
        ) : (
          <CustomButton
            onClick={e => {
              e.stopPropagation()
              toggle(true)
              toggleEditable(true)
            }}
            dark
            icon="edit"
          >
            Edit
          </CustomButton>
        )}
      </div>
      <Divider className="my-0" />
      <Collapse isOpen={isOpen}>
        <div className="p-3 vt-form-grid flex gap-4">
          <div
            id="fsm-beat-basic-form"
            className="grid grid-cols-4 gap-4 w-4/5"
          >
            <FormController
              disableAll
              disabled={!isEditable}
              source={state}
              onChange={store.setData}
            >
              <DynamicSelector
                className="fsm-beat-route"
                path="route"
                label="Route"
                labelElement="surveyArea.properties.name"
                query={ROUTES}
              />
              <DynamicSelector
                className="fsm-beat-asignee-group"
                path="userGroup"
                label="User Group"
                query={GROUPS}
                variables={{
                  category: "SLS",
                }}
              />
              <DynamicSelector
                className="fsm-beat-asignee"
                path="asignee"
                label="User"
                query={USERS}
                name="fullName"
                searchVector={["fullName", "number"]}
                variables={{
                  group: state.userGroup?.name,
                  filter: {
                    surveyAreaId: state?.route?.surveyArea?.id,
                  },
                }}
              />
              <CustomDateInput
                className="fsm-beat-start-date"
                label="Start Date"
                path="startDate"
                big={false}
              />

              <CustomFormGroup
                label="Shift Start Time"
                helperText="The time at which the asignees start their day"
              >
                <TimePicker
                  disabled={!isEditable}
                  onChange={time => store.setData("startTime", time)}
                  value={state?.startTime}
                  useAmPm
                />
              </CustomFormGroup>
              <CustomFormGroup
                label="Shift End Time"
                helperText="The time at which the asignees end their day"
              >
                <TimePicker
                  disabled={!isEditable}
                  onChange={time => store.setData("endTime", time)}
                  value={state?.endTime}
                  useAmPm
                />
              </CustomFormGroup>
            </FormController>
          </div>
          <div className="w-1/5 h-max">
            <CustomFormGroup
              className="fsm-beat-schedule w-max h-max"
              label="Reccurence"
            >
              <CustomButton
                disabled={!isEditable}
                onClick={() => {
                  setState({ run: false, stepIndex: walkState.stepIndex + 1 })
                  toggleDialog()
                  setTimeout(() => {
                    setState({ run: true })
                  }, 400)
                }}
                icon="repeat"
                intent={state.recurrence ? "primary" : "none"}
                text={state?.recurrence ? "View Schedule" : "Configure"}
              />
            </CustomFormGroup>
          </div>
        </div>
      </Collapse>
      <CustomDialog
        footerShowCloseButton={false}
        footerRightButton={
          <CustomButton
            dark
            id="rule-save"
            icon="endorsed"
            text={"Save"}
            onClick={() => {
              setState({ run: false, stepIndex: walkState.stepIndex + 1 })
              toggleDialog(false)
              setState({ run: true })
            }}
          />
        }
        width={"50%"}
        className="h-full"
        light
        title="Configure Recurrence"
        isOpen={isDialogOpen}
        onClose={() => toggleDialog(false)}
      >
        <RRuleGenerator
          onAdd={() => {
            setState({ run: false, stepIndex: walkState.stepIndex + 1 })
            setTimeout(() => {
              setState({ run: true })
            }, 200)
          }}
          rule={state?.recurrence}
          onChange={rule => store.setData("recurrence", rule)}
        />
      </CustomDialog>
    </CustomCard>
  )
}

export default BeatForm
